import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { LucideIcon } from './LucideIcon';
import type { Icon as Interface } from '../../design/components';

const ICONS = [
  'Home', 'Menu', 'X', 'Search', 'Settings', 'MoreVertical', 'MoreHorizontal',
  'Plus', 'Minus', 'Pencil', 'Trash2', 'Save', 'Check', 'CheckCircle',
  'RefreshCw', 'RefreshCcw', 'Undo', 'Redo',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'ChevronLeft', 'ChevronRight', 'ChevronDown', 'ChevronUp',
  'Info', 'AlertTriangle', 'AlertCircle', 'HelpCircle', 'Clock', 'Ban', 'Flag',
  'User', 'UserPlus', 'Users', 'CircleUser', 'Smile',
  'Mail', 'Send', 'Phone', 'MessageCircle', 'MessageSquare', 'Bell',
  'Folder', 'FolderOpen', 'File', 'FileText', 'StickyNote',
  'Paperclip', 'Link', 'Copy', 'Upload', 'Download',
  'Image', 'Camera', 'Video', 'Mic', 'MicOff', 'Play', 'Pause', 'Square',
  'Eye', 'EyeOff', 'List', 'LayoutGrid', 'Grid3X3', 'Table',
  'LayoutDashboard', 'Filter', 'ArrowUpDown', 'Maximize', 'Minimize', 'ZoomIn', 'ZoomOut',
  'BarChart3', 'PieChart', 'TrendingUp', 'TrendingDown',
  'Calendar', 'CalendarDays', 'Timer', 'AlarmClock', 'History',
  'MapPin', 'Map', 'Compass', 'Navigation', 'Globe', 'Languages',
  'ShoppingCart', 'Store', 'Tag', 'CreditCard', 'Wallet',
  'Shield', 'ShieldCheck', 'Lock', 'Unlock', 'Key',
  'Code', 'Terminal', 'Braces', 'Database', 'Bug', 'Cloud',
  'Building2', 'Briefcase', 'ClipboardList', 'ListChecks', 'Layers',
  'Bot', 'Sparkles', 'Lightbulb', 'Hub', 'QrCode', 'Printer', 'Share', 'GitBranch', 'Rocket',
];

export const Icon: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'Select an icon',
  hidden,
  inline,
  readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const filteredIcons = React.useMemo(() => {
    if (!search) return ICONS;
    const lower = search.toLowerCase();
    return ICONS.filter(iconName => iconName.toLowerCase().includes(lower));
  }, [search]);

  if (inline) {
    return value ? (
      <LucideIcon name={value} className={cn(hidden && 'hidden')} size="small" />
    ) : null;
  }

  const handleSelect = (iconName: string) => {
    setValue(iconName);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
      {name && (
        <Label htmlFor={name} className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <div className="flex gap-1">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              id={name}
              disabled={disabled || readonly}
              className={cn(
                'flex-1 justify-start font-normal',
                !value && 'text-muted-foreground',
                error && 'border-destructive'
              )}
            >
              <span className="flex items-center gap-2 flex-1">
                {value ? (
                  <LucideIcon name={value} size="small" />
                ) : (
                  <LucideIcon name="HelpCircle" size="small" className="text-muted-foreground" />
                )}
                <span>{value || placeholder}</span>
              </span>
            </Button>
          </PopoverTrigger>
          {value && !readonly && !disabled && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setValue('')}
            >
              <LucideIcon name="X" size="small" />
            </Button>
          )}
        </div>

        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-2 border-b">
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="h-8"
            />
          </div>

          <div className="p-2 grid grid-cols-6 gap-1 max-h-[300px] overflow-y-auto">
            {filteredIcons.length === 0 ? (
              <p className="col-span-full text-center text-sm text-muted-foreground py-4">
                No icons found
              </p>
            ) : (
              filteredIcons.map((iconName) => {
                const isSelected = value === iconName;
                return (
                  <Tooltip key={iconName}>
                    <TooltipTrigger asChild>
                      <Button
                        variant={isSelected ? 'default' : 'ghost'}
                        size="icon"
                        onClick={() => handleSelect(iconName)}
                        className="h-9 w-9"
                      >
                        <LucideIcon name={iconName} size="small" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{iconName}</TooltipContent>
                  </Tooltip>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      {(error || description) && (
        <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error || description}
        </p>
      )}
    </div>
  );
};
